import c from 'classnames'
import React from 'react'
import { FiCheck, FiPlus } from 'react-icons/fi'
import Text from '../../../../../../components/atoms/text/Text'
import Avatar from '../../../../../../components/molecules/avatar/Avatar'
import { useHolidays } from '../../../../../../modules/seats/hooks'
import { IMeetingRoomSlot, IWorkmate } from '../../../../../../types/app.types'
import { VARIANTS } from '../../../../../../types/Components.types'
import styles from './SlotsList.module.css'

interface SlotsListProps {
  slots: IMeetingRoomSlot[]
  date: Date
  selectedSlots?: string[]
  minSlotDisabled?: number
  maxSlotDisabled?: number
  onSlotSelected?: (hour: string) => void
  onSlotDeselected?: (hour: string) => void
  'aria-label'?: string
}

interface FreeSlotProps {
  slot: IMeetingRoomSlot
  isSelected?: boolean
  onSelect?: (hour: string) => void
  onDeselect?: (hour: string) => void
  disabled?: boolean
}

interface ReservedSlotProps {
  workmates?: IWorkmate['user'][]
  disabled?: boolean
}

const FreeSlot: React.FC<FreeSlotProps> = ({ slot, isSelected = false, onSelect, onDeselect, disabled }) => {
  const handleClick = () => {
    if (isSelected) {
      onDeselect?.(slot.hour)
    } else {
      onSelect?.(slot.hour)
    }
  }

  return (
    <button
      className={c(styles.slotState, styles.freeSlot, { [styles.selectedSlot]: isSelected })}
      onClick={handleClick}
      aria-pressed={isSelected}
      disabled={disabled}
    >
      {isSelected ? <FiCheck size={16} color="rgba(var(--clrPrimary))" /> : <FiPlus size={16} />}
      <Text size="m" weight={500} variant={isSelected ? VARIANTS.primary : VARIANTS.max}>
        {isSelected ? 'Seleccionado' : 'Reservar'}
      </Text>
    </button>
  )
}

const ReservedSlot: React.FC<ReservedSlotProps> = ({ workmates, disabled }) => {
  return (
    <button className={styles.slotState} disabled={disabled}>
      {workmates ? (
        workmates.map((workmate) => {
          return <Avatar key={workmate.id} className={styles.avatar} resource={workmate.facephoto} />
        })
      ) : (
        <Text size="m" weight={500} variant={VARIANTS.secondary}>
          Ocupado
        </Text>
      )}
    </button>
  )
}

const SlotsList: React.FC<SlotsListProps> = ({
  slots,
  date,
  selectedSlots = [],
  minSlotDisabled,
  maxSlotDisabled,
  onSlotSelected,
  onSlotDeselected,
  'aria-label': ariaLabel,
}) => {
  const { data: holidays } = useHolidays()

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    })
  }

  // Check if the selected date is a holiday
  const isHoliday = React.useMemo(() => {
    if (!holidays || !date) return false

    const formattedDate = date.toISOString().split('T')[0] // Format as YYYY-MM-DD
    return holidays.some((holiday) => {
      const holidayDate = new Date(holiday).toISOString().split('T')[0]
      return holidayDate === formattedDate
    })
  }, [holidays, date])

  // Check if there are any slots
  if (slots.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Text size="m" weight={500}>
          No hay horarios disponibles para {formatDate(date)}.
        </Text>
      </div>
    )
  }

  return (
    <section className={styles.container} aria-label={ariaLabel}>
      {isHoliday && (
        <div className={styles.holidayWarning} role="alert" aria-live="polite">
          <Text size="m" weight={500} variant={VARIANTS.warning} align="center">
            ⚠️ {formatDate(date)} es día festivo y el Coworking permanecerá cerrado.
          </Text>
        </div>
      )}
      {slots.map((slot) => {
        const hour = new Date(slot.hour).getHours()
        const isFree = slot.status === 'free'
        const isSelected = selectedSlots.includes(slot.hour)
        const isDisabled =
          isHoliday ||
          (minSlotDisabled ? hour < minSlotDisabled : false) ||
          (maxSlotDisabled ? hour > maxSlotDisabled : false)

        return (
          <article key={hour}>
            <div className={styles.hour}>
              <Text className={styles.label} size="m" weight={500}>
                {String(hour).padStart(2, '0')}:00
              </Text>

              <span className={styles.line}></span>
            </div>

            <div className={styles.slot}>
              {isFree ? (
                <FreeSlot
                  disabled={isDisabled}
                  slot={slot}
                  isSelected={isSelected}
                  onSelect={onSlotSelected}
                  onDeselect={onSlotDeselected}
                />
              ) : (
                <ReservedSlot disabled={isHoliday} />
              )}
            </div>
          </article>
        )
      })}
    </section>
  )
}

export default SlotsList
